import {getLogger} from './logging'
import {
  pushDataLayerEvent,
  getOrCreateVisitorData,
  getCurrentPageInfo,
  createBaseEventData
} from './analyticsHelpers'
import {
  STANDARD_EVENTS,
  DataLayerEvent,
  StandardErrorEvent
} from './analyticsInterfaces'

const logger = getLogger('errorAnalytics')

/**
 * Tracks login errors and authentication issues
 */
export function trackLoginError(
  errorType:
    | 'invalid_credentials'
    | 'account_locked'
    | 'password_reset_required'
    | 'sso_failure'
    | 'network_error'
    | 'unknown_error',
  errorMessage?: string,
  loginMethod?:
    | 'email_password'
    | 'sso_google'
    | 'sso_facebook'
    | 'confirmation_code',
  userId?: string
): void {
  try {
    const baseData = createBaseEventData(userId)

    const event: StandardErrorEvent = {
      event: STANDARD_EVENTS.LOGIN_ERROR,
      ...baseData,
      error_category: 'authentication',
      error_type: errorType,
      error_message: errorMessage,
      error_context: {login_method: loginMethod}
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking login error',
      data: {error}
    })
  }
}

/**
 * Tracks SSO (Single Sign-On) failures
 */
export function trackSSOFailure(
  provider: 'google' | 'facebook' | 'apple' | 'microsoft',
  errorType:
    | 'authentication_failed'
    | 'user_cancelled'
    | 'network_error'
    | 'provider_error'
    | 'account_not_found'
    | 'email_mismatch',
  errorMessage?: string,
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_sso_failure',
      provider,
      error_type: errorType,
      error_message: errorMessage,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }
    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking SSO failure',
      data: {error}
    })
  }
}

/**
 * Tracks form abandonment with context
 */
export function trackFormAbandonment(
  formType:
    | 'login'
    | 'registration'
    | 'profile_edit'
    | 'booking'
    | 'payment'
    | 'contact_info',
  abandonmentReason:
    | 'too_many_fields'
    | 'validation_errors'
    | 'technical_issues'
    | 'user_confusion'
    | 'time_constraints'
    | 'privacy_concerns',
  fieldsCompleted?: number,
  totalFields?: number,
  timeSpent?: number, // seconds
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_form_abandonment',
      form_type: formType,
      abandonment_reason: abandonmentReason,
      fields_completed: fieldsCompleted,
      total_fields: totalFields,
      completion_percentage: totalFields
        ? Math.round(((fieldsCompleted || 0) / totalFields) * 100)
        : undefined,
      time_spent_seconds: timeSpent,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }
    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking form abandonment',
      data: {error}
    })
  }
}

// Legacy trackRepeatedErrors function removed - now using trackFormSubmission and trackValidationError

/**
 * Tracks form submission events
 */
export function trackFormSubmission(
  formType:
    | 'create_account'
    | 'password_reset_request'
    | 'password_reset_confirm',
  success: boolean,
  errorMessage?: string,
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_form_submission',
      form_type: formType,
      success,
      error_message: errorMessage,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }
    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking form submission',
      data: {error, formType, success}
    })
  }
}

/**
 * Tracks validation errors
 */
export function trackValidationError(
  formType:
    | 'create_account'
    | 'password_reset_request'
    | 'password_reset_confirm',
  fieldName: string,
  errorMessage: string,
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_validation_error',
      form_type: formType,
      field_name: fieldName,
      error_message: errorMessage,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking validation error',
      data: {error, formType, fieldName}
    })
  }
}

/**
 * Helper functions for easy error tracking
 */
export const ErrorTrackingExamples = {
  // Login error tracking
  onLoginError: (
    errorType:
      | 'invalid_credentials'
      | 'account_locked'
      | 'password_reset_required'
      | 'sso_failure'
      | 'network_error'
      | 'unknown_error',
    errorMessage?: string,
    loginMethod?:
      | 'email_password'
      | 'sso_google'
      | 'sso_facebook'
      | 'confirmation_code',
    attemptCount?: number,
    userId?: string
  ) => {
    trackLoginError(errorType, errorMessage, loginMethod, userId)
  },

  onSSOFailure: (
    provider: 'google' | 'facebook' | 'apple' | 'microsoft',
    errorType:
      | 'authentication_failed'
      | 'user_cancelled'
      | 'network_error'
      | 'provider_error'
      | 'account_not_found'
      | 'email_mismatch',
    errorMessage?: string,
    userId?: string
  ) => {
    trackSSOFailure(provider, errorType, errorMessage, userId)
  },

  onPasswordReset: (
    formType: 'password_reset_request' | 'password_reset_confirm',
    success: boolean,
    errorMessage?: string,
    userId?: string
  ) => {
    trackFormSubmission(formType, success, errorMessage, userId)
  },

  // Form validation error tracking - now using trackValidationError directly

  onFormAbandonment: (
    formType:
      | 'login'
      | 'registration'
      | 'profile_edit'
      | 'booking'
      | 'payment'
      | 'contact_info',
    abandonmentReason:
      | 'too_many_fields'
      | 'validation_errors'
      | 'technical_issues'
      | 'user_confusion'
      | 'time_constraints'
      | 'privacy_concerns',
    fieldsCompleted?: number,
    totalFields?: number,
    timeSpent?: number,
    userId?: string
  ) => {
    trackFormAbandonment(
      formType,
      abandonmentReason,
      fieldsCompleted,
      totalFields,
      timeSpent,
      userId
    )
  }
}

/**
 * Sets up automatic error tracking
 */
export function setupErrorTracking(): void {
  try {
    // Track unhandled JavaScript errors
    window.addEventListener('error', (event: ErrorEvent) => {
      const baseData = createBaseEventData()
      const errorEvent: StandardErrorEvent = {
        event: STANDARD_EVENTS.LOGIN_ERROR,
        ...baseData,
        error_category: 'javascript',
        error_type: 'unhandled_error',
        error_message: event.message,
        error_context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack
        }
      }
      pushDataLayerEvent(errorEvent)
      console.warn('Unhandled JavaScript error:', event)
    })

    // Track unhandled promise rejections
    window.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        const baseData = createBaseEventData()
        const errorEvent: StandardErrorEvent = {
          event: STANDARD_EVENTS.LOGIN_ERROR,
          ...baseData,
          error_category: 'promise',
          error_type: 'unhandled_rejection',
          error_message: event.reason?.message || String(event.reason),
          error_context: {
            stack: event.reason?.stack,
            reason: event.reason
          }
        }
        pushDataLayerEvent(errorEvent)
        console.warn('Unhandled Promise rejection:', event)
      }
    )
  } catch (error) {
    logger.error({
      message: 'Error setting up error tracking',
      data: {error}
    })
  }
}
